/**
 * Reçu de cotisation : branding org, aperçu HTML, PDF (jspdf + html2canvas), partage.
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ORGANIZATION_DISPLAY_NAME } from '../config/branding';
import { getResolvedPortalTitle, getResolvedSupportEmail } from './portalBranding';

/** Palette Entrea (alignée tailwind.config.js) — défaut si pas de couleur org. */
export const ENTREA_RECEIPT_COLORS = {
  navy: '#16324A',
  ink: '#1F4668',
  royal: '#2f6fd4',
  sky: '#5fa8ff',
  surface: '#f4f7fb',
  panel: '#eaf1f8',
  success: '#159f6d',
  warn: '#d48b00',
  text: '#0f3350',
  muted: '#5b7388',
  line: '#d7e0ea',
  white: '#ffffff',
};

function normalizeHex(value, fallback) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

function hexToRgb(hex) {
  const h = normalizeHex(hex, ENTREA_RECEIPT_COLORS.royal).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** Assombrit / éclaircit une couleur hex pour dégradés. */
export function shadeHex(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const t = amount < 0 ? 0 : 255;
  const p = Math.min(1, Math.abs(amount));
  const nr = Math.round((t - r) * p + r);
  const ng = Math.round((t - g) * p + g);
  const nb = Math.round((t - b) * p + b);
  return `#${[nr, ng, nb].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Branding reçu depuis settings organisation / projet.
 */
export function resolveReceiptBranding(settings) {
  const org = settings?.organization && typeof settings.organization === 'object'
    ? settings.organization
    : {};
  const pp = settings?.project_portal && typeof settings.project_portal === 'object'
    ? settings.project_portal
    : {};
  const primary = normalizeHex(
    org.primary_color_hex || org.primary_color || pp.primary_color_hex,
    ENTREA_RECEIPT_COLORS.royal
  );
  const navy = normalizeHex(org.secondary_color_hex || org.navy_color, ENTREA_RECEIPT_COLORS.navy);
  const logoUrl =
    String(org.logo_url || pp.workspace_logo_url || pp.logo_url || '').trim();

  return {
    orgName: getResolvedPortalTitle(settings),
    orgFullName: String(org.full_name || '').trim(),
    logoUrl,
    primary,
    primaryDark: shadeHex(primary, -0.28),
    primarySoft: shadeHex(primary, 0.88),
    navy,
    sky: ENTREA_RECEIPT_COLORS.sky,
    surface: ENTREA_RECEIPT_COLORS.surface,
    panel: ENTREA_RECEIPT_COLORS.panel,
    text: ENTREA_RECEIPT_COLORS.text,
    muted: ENTREA_RECEIPT_COLORS.muted,
    line: ENTREA_RECEIPT_COLORS.line,
    success: ENTREA_RECEIPT_COLORS.success,
    address: String(org.address || '').trim(),
    phone: String(org.phone || '').trim(),
    email: String(org.email || getResolvedSupportEmail(settings) || '').trim(),
    website: String(org.website || '').trim(),
    registrationNumber: String(org.registration_number || '').trim(),
  };
}

export function formatReceiptAmount(amount, locale = 'fr-FR') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(locale);
}

export function buildReceiptLines({
  orgName,
  memberName,
  contributions = [],
  statusLabel,
  issuedAt,
  labels = {},
  branding = null,
}) {
  const L = {
    title: labels.title || 'Reçu de cotisation',
    org: labels.org || 'Organisation',
    member: labels.member || 'Membre',
    issued: labels.issued || 'Émis le',
    ref: labels.ref || 'Réf.',
    period: labels.period || 'Période',
    amount: labels.amount || 'Montant',
    method: labels.method || 'Paiement',
    status: labels.status || 'Statut',
    notes: labels.notes || 'Notes',
    total: labels.total || 'Total',
    currency: labels.currency || 'FBU',
    footer: labels.footer || 'Document généré par Entrea — à conserver comme preuve de paiement.',
  };

  const name = orgName || branding?.orgName || ORGANIZATION_DISPLAY_NAME;
  const rows = Array.isArray(contributions) ? contributions.filter(Boolean) : [];
  const total = rows.reduce((s, c) => s + (Number(c?.amount) || 0), 0);
  const lines = [
    L.title,
    '—'.repeat(28),
    `${L.org} : ${name}`,
    `${L.member} : ${memberName || '—'}`,
    `${L.issued} : ${issuedAt || '—'}`,
    '',
  ];

  rows.forEach((c, idx) => {
    if (rows.length > 1) lines.push(`#${idx + 1}`);
    if (c.id) lines.push(`${L.ref} : ${c.id}`);
    lines.push(`${L.period} : ${c.periodLabel || c.month || '—'}`);
    lines.push(`${L.amount} : ${formatReceiptAmount(c.amount)} ${L.currency}`);
    lines.push(`${L.method} : ${c.payment_method || '—'}`);
    lines.push(`${L.status} : ${c.statusLabel || statusLabel || c.status || '—'}`);
    if (c.notes) lines.push(`${L.notes} : ${c.notes}`);
    lines.push('');
  });

  if (rows.length > 1) {
    lines.push(`${L.total} : ${formatReceiptAmount(total)} ${L.currency}`);
    lines.push('');
  }
  lines.push(L.footer);
  return lines.join('\n');
}

export function buildReceiptHtml({
  orgName,
  logoUrl,
  memberName,
  contributions = [],
  statusLabel,
  issuedAt,
  labels = {},
  branding = null,
  qrDataUrl = '',
}) {
  const b = branding || {
    orgName,
    logoUrl,
    primary: ENTREA_RECEIPT_COLORS.royal,
    primaryDark: ENTREA_RECEIPT_COLORS.navy,
    primarySoft: ENTREA_RECEIPT_COLORS.panel,
    navy: ENTREA_RECEIPT_COLORS.navy,
    text: ENTREA_RECEIPT_COLORS.text,
    muted: ENTREA_RECEIPT_COLORS.muted,
    line: ENTREA_RECEIPT_COLORS.line,
    surface: ENTREA_RECEIPT_COLORS.surface,
    address: '',
    phone: '',
    email: '',
  };
  const L = {
    title: labels.title || 'Reçu de cotisation',
    org: labels.org || 'Organisation',
    member: labels.member || 'Membre',
    issued: labels.issued || 'Émis le',
    ref: labels.ref || 'Réf.',
    period: labels.period || 'Période',
    amount: labels.amount || 'Montant',
    method: labels.method || 'Paiement',
    status: labels.status || 'Statut',
    notes: labels.notes || 'Notes',
    total: labels.total || 'Total',
    currency: labels.currency || 'FBU',
    footer: labels.footer || 'Document généré par Entrea — à conserver comme preuve de paiement.',
  };
  const rows = Array.isArray(contributions) ? contributions.filter(Boolean) : [];
  const total = rows.reduce((s, c) => s + (Number(c?.amount) || 0), 0);
  const esc = (v) =>
    String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const displayName = esc(b.orgFullName || orgName || b.orgName || '—');
  const logoSrc = logoUrl || b.logoUrl;
  const logo = logoSrc
    ? `<img src="${esc(logoSrc)}" alt="" class="logo" />`
    : `<div class="logo-fallback">${esc((orgName || b.orgName || 'E').slice(0, 1).toUpperCase())}</div>`;

  const contactBits = [b.address, b.phone, b.email, b.website].filter(Boolean).map(esc);
  const refIds = rows.map((c) => String(c?.id || '').trim()).filter(Boolean);
  const primaryRef = refIds.length === 1 ? refIds[0] : refIds.join('|');
  const qrBlock =
    qrDataUrl && primaryRef
      ? `<div class="qr-row">
          <img src="${esc(qrDataUrl)}" alt="QR" class="qr" />
          <div>
            <div class="muted" style="margin:0 0 4px">${esc(L.ref)}</div>
            <div style="font-size:11px;word-break:break-all;font-weight:600">${esc(primaryRef)}</div>
          </div>
        </div>`
      : '';
  const rowHtml = rows
    .map((c) => {
      const notes = c.notes
        ? `<tr><td>${esc(L.notes)}</td><td>${esc(c.notes)}</td></tr>`
        : '';
      return `
      <section class="block">
        ${c.id ? `<div class="muted">${esc(L.ref)} ${esc(c.id)}</div>` : ''}
        <table>
          <tr><td>${esc(L.period)}</td><td><strong>${esc(c.periodLabel || c.month || '—')}</strong></td></tr>
          <tr><td>${esc(L.amount)}</td><td class="amount">${esc(formatReceiptAmount(c.amount))} ${esc(L.currency)}</td></tr>
          <tr><td>${esc(L.method)}</td><td>${esc(c.payment_method || '—')}</td></tr>
          <tr><td>${esc(L.status)}</td><td>${esc(c.statusLabel || statusLabel || c.status || '—')}</td></tr>
          ${notes}
        </table>
      </section>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>${esc(L.title)}</title>
  <style>
    body { font-family: "Segoe UI", system-ui, sans-serif; margin: 0; padding: 20px; color: ${b.text}; background: ${b.surface}; }
    .sheet { max-width: 520px; margin: 0 auto; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 8px 28px rgba(22,50,74,0.12); }
    .hero { background: linear-gradient(135deg, ${b.primaryDark || b.navy} 0%, ${b.primary} 55%, ${ENTREA_RECEIPT_COLORS.sky} 140%); color: #fff; padding: 22px 22px 18px; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .logo, .logo-fallback { width: 56px; height: 56px; border-radius: 14px; background: rgba(255,255,255,0.95); object-fit: contain; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 22px; color: ${b.primary}; }
    .kicker { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.9; margin: 0 0 4px; }
    h1 { font-size: 18px; margin: 0; font-weight: 700; line-height: 1.3; word-break: break-word; }
    .org { font-size: 13px; opacity: 0.92; margin-top: 4px; line-height: 1.35; word-break: break-word; }
    .contact { font-size: 11px; opacity: 0.85; margin-top: 10px; line-height: 1.45; }
    .body { padding: 18px 22px 22px; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; background: ${b.primarySoft || b.surface}; border: 1px solid ${b.line}; border-radius: 12px; padding: 12px; margin-bottom: 14px; }
    .meta label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: ${b.muted}; }
    .meta strong { font-size: 13px; color: ${b.text}; }
    .qr-row { display: flex; align-items: center; gap: 12px; border: 1px solid ${b.line}; border-radius: 12px; padding: 10px 12px; margin-bottom: 12px; }
    .qr { width: 72px; height: 72px; border-radius: 8px; background: #fff; }
    .block { border: 1px solid ${b.line}; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    td { padding: 5px 0; vertical-align: top; }
    td:first-child { width: 38%; color: ${b.muted}; }
    .amount { font-size: 20px; font-weight: 800; font-variant-numeric: tabular-nums; color: ${b.primaryDark || b.navy}; }
    .total { margin-top: 6px; font-size: 15px; font-weight: 800; text-align: right; color: ${b.primaryDark || b.navy}; }
    .muted { font-size: 10px; color: ${b.muted}; margin-bottom: 6px; word-break: break-all; }
    .footer { margin-top: 14px; font-size: 11px; color: ${b.muted}; border-top: 1px dashed ${b.line}; padding-top: 12px; }
    @media print { body { background: #fff; padding: 0; } .sheet { box-shadow: none; border-radius: 0; } }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="hero">
      <div class="brand">${logo}<div>
        <p class="kicker">${esc(L.title)}</p>
        <h1>${displayName}</h1>
        ${b.orgFullName && orgName ? `<div class="org">${esc(orgName)}</div>` : ''}
      </div></div>
      ${contactBits.length ? `<div class="contact">${contactBits.join(' · ')}</div>` : ''}
    </div>
    <div class="body">
      <div class="meta">
        <div><label>${esc(L.member)}</label><strong>${esc(memberName || '—')}</strong></div>
        <div><label>${esc(L.issued)}</label><strong>${esc(issuedAt || '—')}</strong></div>
      </div>
      ${qrBlock}
      ${rowHtml}
      ${rows.length > 1 ? `<div class="total">${esc(L.total)} : ${esc(formatReceiptAmount(total))} ${esc(L.currency)}</div>` : ''}
      <div class="footer">${esc(L.footer)}</div>
    </div>
  </div>
</body>
</html>`;
}

export function printReceiptHtml(html) {
  const w = window.open('', '_blank', 'noopener,noreferrer,width=720,height=900');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  const trigger = () => {
    try {
      w.focus();
      w.print();
    } catch {
      // ignore
    }
  };
  if (w.document.readyState === 'complete') {
    setTimeout(trigger, 150);
  } else {
    w.onload = () => setTimeout(trigger, 150);
  }
  return true;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function downloadReceiptText(text, filename = 'recu-cotisation.txt') {
  downloadBlob(new Blob([text], { type: 'text/plain;charset=utf-8' }), filename);
}

export async function copyReceiptText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return 'clipboard';
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  ta.remove();
  if (!ok) throw new Error('copy_failed');
  return 'clipboard';
}

/**
 * Capture un nœud DOM (aperçu reçu) → canvas HTML.
 */
async function captureReceiptCanvas(element) {
  if (!element) throw new Error('missing_element');

  const capture = async (opts) =>
    html2canvas(element, {
      scale: Math.min(2.5, (window.devicePixelRatio || 1) * 2),
      backgroundColor: '#ffffff',
      logging: false,
      ...opts,
    });

  try {
    return await capture({ useCORS: true, allowTaint: false });
  } catch {
    const imgs = Array.from(element.querySelectorAll('img'));
    const prev = imgs.map((img) => img.style.visibility);
    imgs.forEach((img) => {
      img.style.visibility = 'hidden';
    });
    try {
      return await capture({ useCORS: false, allowTaint: false });
    } finally {
      imgs.forEach((img, i) => {
        img.style.visibility = prev[i] || '';
      });
    }
  }
}

function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('canvas_blob_failed'));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * Capture l'aperçu → image PNG partageable dans un chat.
 */
export async function generateReceiptImageFromElement(
  element,
  { filename = 'recu-cotisation.png' } = {}
) {
  const canvas = await captureReceiptCanvas(element);
  const blob = await canvasToBlob(canvas, 'image/png');
  const file = new File([blob], filename, { type: 'image/png' });
  return { blob, filename, file, canvas };
}

/**
 * Capture un nœud DOM (aperçu reçu) → Blob PDF A4.
 */
export async function generateReceiptPdfFromElement(element, { filename = 'recu-cotisation.pdf' } = {}) {
  const canvas = await captureReceiptCanvas(element);
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const maxW = pageWidth - margin * 2;
  const maxH = pageHeight - margin * 2;
  const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  const x = (pageWidth - w) / 2;
  const y = margin;
  pdf.addImage(imgData, 'PNG', x, y, w, h);
  const blob = pdf.output('blob');
  return { blob, filename, file: new File([blob], filename, { type: 'application/pdf' }) };
}

/**
 * Partage le reçu comme image (PNG) pour un chat.
 * - Mobile / navigateurs compatibles : fichier image via Web Share API
 * - Sinon : copie de l'image dans le presse-papiers (à coller dans WhatsApp Web, etc.)
 * - Si un numéro membre est connu : ouvre aussi le chat WhatsApp (sans lien fichier)
 * N'uploade pas et ne télécharge pas le fichier.
 * @returns {'native'|'clipboard'|'whatsapp-clipboard'}
 */
export function normalizeWhatsAppPhone(phone, { defaultCountryCode = '257' } = {}) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 9 && !digits.startsWith(defaultCountryCode)) {
    digits = `${defaultCountryCode}${digits}`;
  } else if (digits.length === 10 && digits.startsWith('0')) {
    digits = `${defaultCountryCode}${digits.slice(1)}`;
  }
  return digits;
}

function openWhatsApp(phoneDigits, message) {
  const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`;
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) window.location.href = url;
}

async function copyImageBlobToClipboard(blob) {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    throw new Error('clipboard_image_unsupported');
  }
  const type = blob.type || 'image/png';
  await navigator.clipboard.write([new ClipboardItem({ [type]: blob })]);
  return 'clipboard';
}

export async function shareReceiptToMember({
  phone,
  title,
  text,
  file,
  orgName = '',
  memberName = '',
  reference = '',
}) {
  const imageFile =
    file instanceof File
      ? file
      : file
        ? new File([file], 'recu-cotisation.png', { type: 'image/png' })
        : null;
  if (!imageFile || !String(imageFile.type || '').startsWith('image/')) {
    const err = new Error('missing_image');
    err.code = 'missing_image';
    throw err;
  }

  const shareTitle = title || 'Reçu de cotisation';
  const waPhone = normalizeWhatsAppPhone(phone);
  const caption =
    [
      shareTitle,
      orgName ? String(orgName) : '',
      memberName ? `Membre : ${memberName}` : '',
      reference ? `Réf. : ${reference}` : '',
    ]
      .filter(Boolean)
      .join('\n') || shareTitle;

  // 1) Fichier image réel via partage système (WhatsApp, Telegram, etc.)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    const withFile = {
      title: shareTitle,
      text: caption,
      files: [imageFile],
    };
    const canFiles =
      typeof navigator.canShare !== 'function' || navigator.canShare({ files: [imageFile] });
    if (canFiles) {
      try {
        await navigator.share(withFile);
        return 'native';
      } catch (err) {
        if (err?.name === 'AbortError') throw err;
      }
    }
  }

  // 2) Desktop : coller l'image dans le chat (wa.me ne peut pas joindre un fichier)
  try {
    await copyImageBlobToClipboard(imageFile);
    if (waPhone) {
      openWhatsApp(
        waPhone,
        `${caption}\n\n(Collez l'image du reçu dans ce chat — Ctrl+V / appui long → Coller)`
      );
      return 'whatsapp-clipboard';
    }
    return 'clipboard';
  } catch {
    // ignore → erreur finale
  }

  const err = new Error('share_unavailable');
  err.code = 'share_unavailable';
  throw err;
}

/** @deprecated Utiliser shareReceiptToMember */
export async function shareReceiptPdf(opts) {
  return shareReceiptToMember(opts);
}
